
import { supabase } from "@/integrations/supabase/client";
import { Guard } from "../types";

/**
 * Restores an archived guard to active status and re-enables their credentials
 */
export const restoreGuard = async (guardId: string): Promise<Guard> => {
  try {
    console.log(`Restoring guard with ID: ${guardId}`);
    
    // Use the dedicated edge function to restore the guard
    // This function will re-enable the auth user and update the profile
    const { data, error } = await supabase.functions.invoke('restore-guard', {
      body: { guardId }
    });

    if (error) {
      console.error('Error restoring guard:', error);
      throw new Error(error.message || 'Failed to restore guard');
    }

    console.log('Guard restore response:', data);
    
    // Also make sure user_roles is properly restored if it was missing
    try {
      // Check if a role record exists
      const { data: existingRoleData, error: roleCheckError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', guardId);
      
      if (roleCheckError) {
        console.error('Error checking existing role:', roleCheckError);
      } else if (!existingRoleData || existingRoleData.length === 0) {
        // If no role exists, create a default 'guard' role
        const { error: roleInsertError } = await supabase
          .from('user_roles')
          .insert({ user_id: guardId, role: 'guard' });
        
        if (roleInsertError) {
          console.error('Error inserting default user role:', roleInsertError);
        } else {
          console.log(`Created default 'guard' role for restored user ${guardId}`);
        }
      }
    } catch (roleError) {
      console.error('Error handling role restoration:', roleError);
      // Don't throw here, as the main restore operation was successful
    }
    
    // If the guard has an email, send them their login credentials
    if (data.profile.email) {
      try {
        await supabase.functions.invoke('send-credentials', {
          body: { 
            clientId: guardId,
            isRestored: true
          }
        });
        console.log('Sent login credentials to restored guard');
      } catch (emailError) {
        console.error('Error sending credentials email:', emailError);
        // Don't throw here, as the main restore operation was successful
      }
    }
    
    return data.profile as Guard;
  } catch (error) {
    console.error('Error in restoreGuard:', error);
    throw error;
  }
};
